import { useUploadGuard } from '../hooks/useUploadGuard';
import React from 'react';
import { Alert, Box, Button, Paper, Typography } from '@mui/material';
import type { StandaloneScopeDocument } from '../api/constructionPriceEstimatorFunctions';
import ResizableRequirementTextarea from '../components/ResizableRequirementTextarea';

interface Props {
  flowId: string;
  value: string;
  document: StandaloneScopeDocument | null | undefined;
  canEdit: boolean;
  busy: boolean;
  onChange: (value: string) => void;
  onBlur: () => void;
  onUpload: (file: File | null) => void;
  onRemove: () => void;
}

const documentStatusText = (status: StandaloneScopeDocument['extraction_status']): string => {
  if (status === 'ready') return 'text extracted';
  if (status === 'rejected' || status === 'unreadable' || status === 'failed') {
    return 'text could not be extracted; upload another SOW or enter a description';
  }
  return 'text extraction in progress';
};

export const ConstructionPriceEstimatorScope: React.FC<Props> = ({
  flowId, value, document, canEdit, busy, onChange, onBlur, onUpload, onRemove,
}) => {
  const { disabledRef } = useUploadGuard(!canEdit || busy);
  return <Paper data-tour-id="price-scope" component="section" variant="outlined"
  sx={{ p: { xs: 2, md: 2.5 }, mb: 2 }} aria-labelledby="project-scope-heading">
  <Typography id="project-scope-heading" variant="h5" component="h2" gutterBottom>Project scope</Typography>
  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
    Provide a project description, upload an SOW, or both. At least one usable scope input is required before Contract Details can be filled.
  </Typography>
  <ResizableRequirementTextarea label="Project description (optional when an SOW is uploaded)"
    resizeKey={`construction-price:${flowId}:project-description`}
    value={value} onChange={(event) => onChange(event.target.value)} textareaProps={{ onBlur }}
    disabled={!canEdit}
    helperText="Use this for a concise scope summary or supplemental context." />
  <Box sx={{ mt: 2 }}>
    <Typography component="label" htmlFor="price-sow-upload" sx={{ display: 'block', mb: 0.5 }}>
      Upload SOW (optional when a description is provided)
    </Typography>
    <Button component="label" variant="outlined" disabled={!canEdit || busy}>
      {busy ? 'Uploading SOW…' : 'Upload SOW'}
      <input id="price-sow-upload" aria-label="Upload SOW" hidden type="file" disabled={!canEdit || busy}
        accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
        onChange={(event) => { if (!disabledRef.current) onUpload(event.target.files?.[0] ?? null); event.currentTarget.value = ''; }} />
    </Button>
    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
      PDF, DOCX, or UTF-8 text. The server validates and extracts the document privately.
    </Typography>
    {document && <Alert severity={document.extraction_status === 'ready' ? 'success' : 'warning'} sx={{ mt: 1 }}
      action={<Button color="inherit" size="small" disabled={!canEdit || busy} onClick={onRemove}>Remove</Button>}>
      Uploaded SOW: {document.filename} · {documentStatusText(document.extraction_status)}
    </Alert>}
  </Box>
</Paper>;
};
